/**
 * useGraphFilters Hook
 * Manages filter state for graph visualization
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * Hook for managing graph filter state
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Filter state and handlers
 */
export function useGraphFilters(initialFilters = {}) {
    const [filters, setFilters] = useState({
        sector: initialFilters.sector || '',
        region: initialFilters.region || '',
        programming: initialFilters.programming || '',
        searchTerm: initialFilters.searchTerm || '',
        colorBy: initialFilters.colorBy || 'sector',
        hasWebsite: initialFilters.hasWebsite || '',
        hasDescription: initialFilters.hasDescription || '',
        minConnections: initialFilters.minConnections || '',
    });

    // Sync filters when initialFilters change
    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            sector: initialFilters.sector || '',
            region: initialFilters.region || '',
            programming: initialFilters.programming || '',
            searchTerm: initialFilters.searchTerm || '',
            hasWebsite: initialFilters.hasWebsite || '',
            hasDescription: initialFilters.hasDescription || '',
            minConnections: initialFilters.minConnections || '',
        }));
    }, [
        initialFilters.sector,
        initialFilters.region,
        initialFilters.programming,
        initialFilters.searchTerm,
        initialFilters.hasWebsite,
        initialFilters.hasDescription,
        initialFilters.minConnections,
    ]);

    // Update a single filter
    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    // Update multiple filters at once
    const setMultipleFilters = useCallback((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
    }, []);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setFilters({
            sector: '',
            region: '',
            programming: '',
            searchTerm: '',
            colorBy: 'sector',
            hasWebsite: '',
            hasDescription: '',
            minConnections: '',
        });
    }, []);

    // Check if any filters are active (excluding colorBy and searchTerm)
    const hasActiveFilters = useMemo(() => {
        return !!(
            filters.sector ||
            filters.region ||
            filters.programming ||
            filters.hasWebsite ||
            filters.hasDescription ||
            filters.minConnections
        );
    }, [
        filters.sector,
        filters.region,
        filters.programming,
        filters.hasWebsite,
        filters.hasDescription,
        filters.minConnections,
    ]);

    // Get API-compatible filter object
    const apiFilters = useMemo(
        () => ({
            sector: filters.sector || undefined,
            region: filters.region || undefined,
            programmingTypes: filters.programming || undefined,
            hasWebsite: filters.hasWebsite || undefined,
            hasDescription: filters.hasDescription || undefined,
            minConnections: filters.minConnections || undefined,
        }),
        [
            filters.sector,
            filters.region,
            filters.programming,
            filters.hasWebsite,
            filters.hasDescription,
            filters.minConnections,
        ]
    );

    return {
        filters,
        setFilter,
        setMultipleFilters,
        clearFilters,
        hasActiveFilters,
        apiFilters,
    };
}

export default useGraphFilters;
